import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendAppointmentEmail } from '../services/emailService';

export const OFFICES = [
  'VER TODOS',
  'VER LOS QUE ATIENDEN x DIA',
  'VER x SELECCIÓN',
  'CONSULTORIOS EXTERNOS',
  'ECOGRAFIAS .',
  'FONOAUDIOLOGA .',
  'NEUMONOLOGO .',
  'NEUROLOGO .',
  'OTORRINO .',
  'PSICOLOGIA .',
  'RAYOS X',
  'AGREGAR OTRO PROFESIONAL'
];

export const VIEW_MODES = ['VISTA DIARIA', 'VISTA SEMANAL', 'VISTA MENSUAL'];

export const getLocalISODate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const useCalendar = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const targetDate = searchParams.get('date');
  const targetAppId = searchParams.get('appId');
  
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o';
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedOffice, setSelectedOffice] = useState(isAdmin ? OFFICES[0] : (activeStaff?.full_name || OFFICES[0]));
  const [viewMode, setViewMode] = useState(VIEW_MODES[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailModalData, setEmailModalData] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [professionals, setProfessionals] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const { addNotification } = useNotifications();
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPortalApp, setSelectedPortalApp] = useState(null);
  const [clipboardApp, setClipboardApp] = useState(null);
  const [isFinderOpen, setIsFinderOpen] = useState(false);
  const [expandedAppId, setExpandedAppId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (targetDate) {
      const [year, month, day] = targetDate.split('-');
      setCurrentDate(new Date(year, month - 1, day));
    }
    if (targetAppId) {
      setExpandedAppId(targetAppId);
    }
  }, [targetDate, targetAppId]);

  const toggleExpandApp = (id) => {
    setExpandedAppId(prev => prev === id ? null : id);
  };

  useEffect(() => {
    if (localSearchQuery) setIsSearching(true);
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(localSearchQuery);
      setIsSearching(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [localSearchQuery]);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('appointments').select('*').order('appointment_time', { ascending: true });
    
    if (selectedOffice && !['VER TODOS', 'VER LOS QUE ATIENDEN x DIA', 'VER x SELECCIÓN', 'AGREGAR OTRO PROFESIONAL'].includes(selectedOffice)) {
      query = query.eq('professional_name', selectedOffice);
    }
    
    if (debouncedSearchQuery) {
      const cleanQuery = debouncedSearchQuery.replace(/[^0-9a-zA-Z]/g, '');
      const searchTerms = [
        `patient_name.ilike.%${debouncedSearchQuery}%`,
        `study_type.ilike.%${debouncedSearchQuery}%`,
        `patient_dni.ilike.%${debouncedSearchQuery}%`,
        `patient_dni.ilike.%${cleanQuery}%`,
        `study_details->>patient_dni.ilike.%${debouncedSearchQuery}%`,
        `study_details->>patient_dni.ilike.%${cleanQuery}%`
      ];
      query = query.or(searchTerms.join(','));
    } else {
      if (viewMode === 'VISTA SEMANAL') {
        const start = new Date(currentDate);
        const day = start.getDay() || 7;
        start.setDate(start.getDate() - day + 1);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        query = query.gte('appointment_date', getLocalISODate(start)).lte('appointment_date', getLocalISODate(end));
      } else if (viewMode === 'VISTA MENSUAL') {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const start = new Date(year, month, 1);
        const end = new Date(year, month + 1, 0);
        query = query.gte('appointment_date', getLocalISODate(start)).lte('appointment_date', getLocalISODate(end));
      } else {
        query = query.eq('appointment_date', getLocalISODate(currentDate));
      }
    }

    const { data, error } = await query;
    if (error) {
      addNotification({ title: 'Error', message: `Error al cargar turnos: ${error.message}`, type: 'error' });
    } else {
      setAppointments(data || []);
    }
    setLoading(false);
  }, [currentDate, viewMode, addNotification, debouncedSearchQuery, selectedOffice]);

  const fetchAppointmentsRef = useRef(fetchAppointments);
  
  useEffect(() => {
    fetchAppointmentsRef.current = fetchAppointments;
  }, [fetchAppointments]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const fetchProfessionals = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('professionals').select('*');
      if (!error && data) {
        setProfessionals(data);
      }
    } catch (err) {
      console.error('Error fetching professionals:', err);
    }
  }, []);

  useEffect(() => {
    fetchProfessionals();
  }, [fetchProfessionals]);

  useEffect(() => {
    const subscription = supabase
      .channel('public:appointments')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments' }, () => {
        if (fetchAppointmentsRef.current) fetchAppointmentsRef.current();
      })
      .subscribe();
    return () => supabase.removeChannel(subscription);
  }, []);

  const timeSlots = useMemo(() => {
    const slots = new Set();
    const daysMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    
    // Determine which professionals to consider
    let activeProfs = [];
    if (selectedOffice && !['VER TODOS', 'VER LOS QUE ATIENDEN x DIA', 'VER x SELECCIÓN', 'AGREGAR OTRO PROFESIONAL'].includes(selectedOffice)) {
      const prof = professionals.find(p => p.full_name === selectedOffice);
      if (prof) activeProfs.push(prof);
    } else {
      activeProfs = professionals;
    }

    if (activeProfs.length === 0) {
      // Fallback defaults
      for(let m = 8*60; m <= 21*60; m += 30) {
        slots.add(`${Math.floor(m/60).toString().padStart(2, '0')}:${(m%60).toString().padStart(2, '0')}:00`);
      }
      return Array.from(slots).sort();
    }

    // Determine which dates to check based on viewMode
    const datesToCheck = [];
    if (viewMode === 'VISTA SEMANAL') {
      // Find Monday of the current week
      const d = new Date(currentDate);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(d.setDate(diff));
      for (let i = 0; i < 7; i++) {
        const checkDate = new Date(monday);
        checkDate.setDate(monday.getDate() + i);
        datesToCheck.push(checkDate);
      }
    } else {
      datesToCheck.push(currentDate);
    }

    // Default slot duration
    let minSlotDuration = 30;
    const durations = activeProfs.map(p => parseInt(p.working_hours?.slot_duration || 30, 10)).filter(d => !isNaN(d));
    if (activeProfs.length === 1 && durations.length > 0) minSlotDuration = durations[0]; else minSlotDuration = 30;

    datesToCheck.forEach(dateObj => {
      const dateStr = getLocalISODate(dateObj);
      const dayOfWeek = daysMap[dateObj.getDay()];

      activeProfs.forEach(prof => {
        // Skip if this professional has the day off
        if (prof.off_dates && prof.off_dates.includes(dateStr)) return;
        
        let wh = prof.working_hours?.specific_dates?.[dateStr];
        if (!wh) {
          wh = prof.working_hours?.[dayOfWeek];
        }
        if (!wh || wh.enabled === false) return;

        // Process shift 1
        if (wh.start && wh.end) {
          const [sh, sm] = wh.start.split(':').map(Number);
          const [eh, em] = wh.end.split(':').map(Number);
          let m = sh * 60 + sm;
          const endM = eh * 60 + em;
          while (m <= endM) {
            slots.add(`${Math.floor(m/60).toString().padStart(2, '0')}:${(m%60).toString().padStart(2, '0')}:00`);
            m += minSlotDuration;
          }
        }

        // Process shift 2
        const s2 = wh.start2 || (wh.has_shift2 && wh.end2 ? '14:00' : null);
        if (wh.has_shift2 && s2 && wh.end2) {
          const [sh, sm] = s2.split(':').map(Number);
          const [eh, em] = wh.end2.split(':').map(Number);
          let m = sh * 60 + sm;
          const endM = eh * 60 + em;
          while (m <= endM) {
            slots.add(`${Math.floor(m/60).toString().padStart(2, '0')}:${(m%60).toString().padStart(2, '0')}:00`);
            m += minSlotDuration;
          }
        }
      });
    });

    // Also guarantee that every appointment loaded for this view has its corresponding time slot in the grid
    appointments.forEach(app => {
      if (app.appointment_time && app.appointment_time !== '00:00:00') {
        if (selectedOffice && !['VER TODOS', 'VER LOS QUE ATIENDEN x DIA', 'VER x SELECCIÓN', 'AGREGAR OTRO PROFESIONAL'].includes(selectedOffice)) {
          if (app.professional_name !== selectedOffice) return;
        }
        const timePart = app.appointment_time.substring(0, 5);
        slots.add(`${timePart}:00`);
      }
    });

    return Array.from(slots).sort();
  }, [selectedOffice, professionals, currentDate, viewMode, appointments]);

  const handlePrevDay = () => {
    const d = new Date(currentDate);
    if (viewMode === 'VISTA SEMANAL') d.setDate(d.getDate() - 7);
    else if (viewMode === 'VISTA MENSUAL') d.setMonth(d.getMonth() - 1);
    else d.setDate(d.getDate() - 1);
    setCurrentDate(d);
  };

  const handleNextDay = () => {
    const d = new Date(currentDate);
    if (viewMode === 'VISTA SEMANAL') d.setDate(d.getDate() + 7);
    else if (viewMode === 'VISTA MENSUAL') d.setMonth(d.getMonth() + 1);
    else d.setDate(d.getDate() + 1);
    setCurrentDate(d);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    addNotification({ title: 'Hoy', message: 'Navegando al día de hoy.', type: 'info' });
  };

  const openNewAppointment = (time, profName = null) => {
    if (clipboardApp) {
      setModalData({ 
        ...clipboardApp,
        id: clipboardApp.id,
        patient_id: clipboardApp.patient_id,
        patient: clipboardApp.patient_name || clipboardApp.patient,
        patient_dni: clipboardApp.patient_dni,
        studyType: clipboardApp.study_type,
        time: time, 
        date: getLocalISODate(currentDate), 
        professional: profName || selectedOffice,
        notes: clipboardApp.admin_notes || clipboardApp.notes
      });
      setIsModalOpen(true);
      setClipboardApp(null);
      return;
    }
    setModalData({ time, date: getLocalISODate(currentDate), professional: profName || selectedOffice });
    setIsModalOpen(true);
  };

  const handleSelectSlotFromFinder = (slot) => {
    const [year, month, day] = slot.date.split('-').map(Number);
    const newDate = new Date(year, month - 1, day);
    setCurrentDate(newDate);
    setViewMode('VISTA DIARIA');
    setIsFinderOpen(false);
    setTimeout(() => {
      openNewAppointment(slot.time + ':00');
    }, 100);
  };

  const openNewAppointmentWeekly = (dateStr, time) => {
    if (clipboardApp) {
      setModalData({ 
        ...clipboardApp,
        id: clipboardApp.id,
        patient_id: clipboardApp.patient_id,
        patient: clipboardApp.patient_name || clipboardApp.patient,
        patient_dni: clipboardApp.patient_dni,
        studyType: clipboardApp.study_type,
        time: time, 
        date: dateStr, 
        professional: selectedOffice,
        notes: clipboardApp.admin_notes || clipboardApp.notes
      });
      setIsModalOpen(true);
      setClipboardApp(null);
      return;
    }
    setModalData({ time, date: dateStr, professional: selectedOffice });
    setIsModalOpen(true);
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    const day = start.getDay() || 7;
    start.setDate(start.getDate() - day + 1);
    return [0,1,2,3,4,5,6].map(i => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  };

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    const startPadding = firstDay.getDay() || 7;
    for (let i = 1; i < startPadding; i++) {
      days.push({ date: new Date(year, month, 1 - (startPadding - i)), isCurrentMonth: false });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    return days;
  };

  const saveAppointment = async (data, options = { keepOpen: false }) => {
    try {
      const activeUser = JSON.parse(localStorage.getItem('gdt_active_user') || '{}');
      const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
      const operatorName = activeUser.display_name || activeUser.username || activeStaff.full_name || 'Sistema';

      const payload = {
        patient_id: data.patient_id || null,
        patient_dni: data.patient_dni || null,
        patient_name: data.patient,
        patient_email: data.patient_email || '',
        patient_phone: data.patient_phone || '',
        appointment_date: data.date,
        appointment_time: data.time,
        status: 'pendiente',
        admin_notes: data.notes,
        study_type: data.studyType || 'CONSULTA GENERAL',
        professional_name: data.professional,
        created_by_user: operatorName
      };

      if (data.attached_files && data.attached_files.length > 0) {
        // Obtenemos los adjuntos previos (si los hay) al hacer update, o lo seteamos
        payload.study_details = { attached_files: data.attached_files }; 
      }

      let error;
      if (data.id) {
        const { error: updErr } = await supabase.from('appointments').update(payload).eq('id', data.id);
        error = updErr;
      } else {
        // Prevent duplicate creation for same patient, date, time and professional
        if (payload.appointment_date && payload.appointment_time && payload.professional_name) {
          const normName = (payload.patient_name || '').replace(/[\s\u00a0]+/g, ' ').trim();
          const { data: existing } = await supabase
            .from('appointments')
            .select('id')
            .eq('appointment_date', payload.appointment_date)
            .eq('appointment_time', payload.appointment_time)
            .eq('professional_name', payload.professional_name)
            .ilike('patient_name', normName)
            .maybeSingle();

          if (existing) {
            addNotification({ title: 'Turno Existente', message: `Ya existe un turno registrado para ${payload.patient_name} a las ${payload.appointment_time?.substring(0, 5)} hs.`, type: 'warning' });
            if (!options.keepOpen) setIsModalOpen(false);
            return;
          }
        }
        const { error: insErr } = await supabase.from('appointments').insert([payload]);
        error = insErr;
      }
      if (error) throw error;
      addNotification({ title: 'Turno Guardado', message: `Turno asignado a ${data.patient}.`, type: 'success' });
      if (!options.keepOpen) {
        setIsModalOpen(false);
      }
    } catch (error) {
      addNotification({ title: 'Error', message: `No se pudo guardar: ${error.message}`, type: 'error' });
      throw error; 
    }
  };

  const handleCambiarHorarios = () => setShowScheduleModal(true);
  const handleInfoProf = () => {
    if (!selectedOffice || selectedOffice === 'VER TODOS' || selectedOffice === 'VER LOS QUE ATIENDEN x DIA' || selectedOffice === 'VER x SELECCIÓN' || selectedOffice === 'AGREGAR OTRO PROFESIONAL') {
      addNotification({ title: 'Aviso', message: 'Seleccione un profesional primero.', type: 'warning' });
      return;
    }
    setShowInfoModal(true);
  };

  const handleCreatePatientFromPortal = async (app) => {
    try {
      const { data: newPatient, error: pError } = await supabase
        .from('patients')
        .insert([{
          full_name: app.patient_name,
          
          dni: app.study_details?.patient_dni || '',
          email: app.patient_email || '',
          phone: app.patient_phone || '',
          birthdate: app.study_details?.patient_birthdate || null,
          insurance_provider: app.study_details?.insurance_provider || '',
          insurance_plan: app.study_details?.insurance_plan || ''
        }])
        .select()
        .single();

      if (pError) throw pError;

      const { error: uError } = await supabase
        .from('appointments')
        .update({ patient_id: newPatient.id })
        .eq('id', app.id);

      if (uError) throw uError;

      addNotification({ title: 'Éxito', message: 'Ficha de paciente creada y vinculada.', type: 'success' });
      setSelectedPortalApp(null);
      fetchAppointments();
      navigate(`/patients/${newPatient.id}`);
    } catch (err) {
      addNotification({ title: 'Error', message: err.message, type: 'error' });
    }
  };

  const handleResendPortalEmail = (app) => {
    setEmailModalData(app);
    setEmailInput('info@gestionamientodeturnos.com');
  };

  const handleSendEmail = async () => {
    if (!emailInput || !emailInput.includes('@')) {
      addNotification({ title: 'Error', message: 'Por favor, ingrese un correo válido.', type: 'error' });
      return;
    }
    try {
      addNotification({ title: 'Enviando...', message: 'Procesando el expediente completo...', type: 'info' });
      await sendAppointmentEmail(emailModalData, emailInput);
      addNotification({ title: 'Enviado', message: `Expediente completo enviado a ${emailInput}.`, type: 'success' });
      setEmailModalData(null);
      setEmailInput('');
    } catch (err) {
      addNotification({ title: 'Error al enviar', message: err.message, type: 'error' });
    }
  };

  const deleteAppointment = async (id) => {
    if (window.confirm('¿Eliminar turno?')) {
      await supabase.from('appointments').delete().eq('id', id);
      fetchAppointments();
    }
  };

  const updateAppointmentNotes = async (id, notes) => {
    await supabase.from('appointments').update({ admin_notes: notes }).eq('id', id);
    fetchAppointments();
  };

  const updateAppointmentDetails = async (id, data) => {
    await supabase.from('appointments').update(data).eq('id', id);
    fetchAppointments();
  };

  const updateAppointmentStatus = async (id, newStatus) => {
    if (newStatus === 'en-llamado' || newStatus === 'en-consultorio') {
      const currentApp = appointments.find(a => a.id === id);
      if (currentApp) {
        const patientDni = currentApp.patient_dni || currentApp.study_details?.patient_dni;
        const patientName = currentApp.patient_name;
        const today = currentApp.appointment_date || getLocalISODate(currentDate);

        const { data: busyData } = await supabase.from('appointments')
          .select('id, patient_name, patient_dni, professional_name, consultorio_name, status, appointment_time')
          .eq('appointment_date', today)
          .neq('id', id)
          .in('status', ['en-llamado', 'en-consultorio', 'atendido']);

        if (busyData && busyData.length > 0) {
          const normDni = (patientDni || '').replace(/[^0-9]/g, '').trim();
          const normName = (patientName || '').replace(/[\s\u00a0]+/g, ' ').trim().toLowerCase();

          const conflict = busyData.find(other => {
            const otherDni = (other.patient_dni || '').replace(/[^0-9]/g, '').trim();
            const otherName = (other.patient_name || '').replace(/[\s\u00a0]+/g, ' ').trim().toLowerCase();
            if (normDni && otherDni && normDni === otherDni) return true;
            if (normName && otherName && normName === otherName) return true;
            return false;
          });

          if (conflict) {
            const statusLabel = conflict.status === 'en-consultorio' ? 'siendo atendido' : (conflict.status === 'atendido' ? 'marcado como Atendido' : 'en llamado');
            const roomLabel = conflict.consultorio_name || conflict.professional_name || 'otra área';
            const confirmCall = window.confirm(
              `⚠️ ¡ATENCIÓN! PACIENTE EN ATENCIÓN EN OTRA SALA\n\n` +
              `El paciente "${patientName}" figura actualmente ${statusLabel} en ${roomLabel}.\n\n` +
              `¿Deseas llamarlo de todas formas a esta consulta?`
            );
            if (!confirmCall) return;
          }
        }
      }
    }

    let updateData = { status: newStatus };
    if (newStatus === 'presente') {
      updateData.arrival_time = new Date().toISOString();
    } else if (newStatus === 'pendiente' || newStatus === 'cancelado') {
      updateData.arrival_time = null;
    }
    await supabase.from('appointments').update(updateData).eq('id', id);
    fetchAppointments();
  };

  return {
    clipboardApp, setClipboardApp,
    isAdmin,
    currentDate, setCurrentDate,
    selectedOffice, setSelectedOffice,
    viewMode, setViewMode,
    isModalOpen, setIsModalOpen,
    modalData, setModalData,
    appointments, setAppointments,
    loading, setLoading,
    emailModalData, setEmailModalData,
    emailInput, setEmailInput,
    showScheduleModal, setShowScheduleModal,
    showInfoModal, setShowInfoModal,
    localSearchQuery, setLocalSearchQuery,
    debouncedSearchQuery, setDebouncedSearchQuery,
    isSearching, setIsSearching,
    selectedPortalApp, setSelectedPortalApp,
    isFinderOpen, setIsFinderOpen,
    expandedAppId, setExpandedAppId,
    timeSlots,
    toggleExpandApp,
    handlePrevDay,
    handleNextDay,
    goToToday,
    openNewAppointment,
    handleSelectSlotFromFinder,
    openNewAppointmentWeekly,
    getWeekDays,
    getMonthDays,
    saveAppointment,
    handleCambiarHorarios,
    handleInfoProf,
    handleCreatePatientFromPortal,
    handleResendPortalEmail,
    handleSendEmail,
    deleteAppointment,
    updateAppointmentStatus,
    updateAppointmentNotes,
    updateAppointmentDetails,
    fetchAppointments,
    professionals
  };
};

export const isSlotValidForProf = (prof, dateObj, timeStr) => {
  const daysMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const dateStr = getLocalISODate(dateObj);
  if (prof.off_dates && prof.off_dates.includes(dateStr)) return false;
  
  let wh = prof.working_hours?.specific_dates?.[dateStr];
  if (!wh) {
    wh = prof.working_hours?.[daysMap[dateObj.getDay()]];
  }
  
  if (!wh || (wh.enabled === false && !prof.working_hours?.specific_dates?.[dateStr])) return false;
  
  const [th, tm] = timeStr.split(':').map(Number);
  const timeM = th * 60 + tm;
  if (wh.start && wh.end) {
    const [sh, sm] = wh.start.split(':').map(Number);
    const [eh, em] = wh.end.split(':').map(Number);
    if (timeM >= (sh*60+sm) && timeM < (eh*60+em)) return true;
  }
  if (wh.has_shift2 && wh.end2) {
    const start2Val = wh.start2 || '14:00';
    const [sh, sm] = start2Val.split(':').map(Number);
    const [eh, em] = wh.end2.split(':').map(Number);
    if (timeM >= (sh*60+sm) && timeM < (eh*60+em)) return true;
  }
  return false;
};
